/**
 * web/src/screens/Notebook/hooks/clusters.ts — the clusters/opinions seam of
 * useNotebookState (card vncVrGk1hc9i, round two): this person's cluster
 * prefs in localStorage and the group actions (plan, pick dates, dissolve,
 * remove/add member, restore). Pure move out of the former hooks.ts.
 */
import { useCallback, useState } from 'react';
import { session, type Session } from '../../../shell/bridge';
import { createDateIdea } from '../../../lib/dateIdeas';
import {
  EMPTY_CLUSTER_PREFS, clusterExclKey, clusterPrefKey, readClusterPrefs,
  type ClusterPrefs, type FeedCluster,
} from '../types';
import type { NotebookDates } from './dates';

export function useNotebookClusters({ me, loadDates, setDateSaved }: {
  me: Session | null;
  loadDates: NotebookDates['loadDates'];
  setDateSaved: NotebookDates['setDateSaved'];
}) {
  /* PRD E3/E4 — THIS person's opinion of each group (dissolve / remove / add),
     kept exactly where the List/Grid pick keeps its own: their localStorage,
     under their member id. Nothing is sent anywhere, so one partner hiding a
     group never hides it for the other. */
  const [clusterPrefs, setClusterPrefs] = useState<ClusterPrefs>(() => readClusterPrefs(me?.memberId ?? null));
  const [openCluster, setOpenCluster] = useState<string | null>(null);
  const [clusterAddPick, setClusterAddPick] = useState('');
  const writeClusterPrefs = useCallback((next: ClusterPrefs) => {
    setClusterPrefs(next);
    try {
      window.localStorage.setItem(clusterPrefKey(session()?.memberId ?? null), JSON.stringify(next));
    } catch { /* private mode — the pick still applies for this visit */ }
  }, []);

  /* ── PRD E4 — the cluster's combined actions ────────────────────────────────
     Plan this writes the group's own label into the date pool through the SAME
     POST the capture bar's "date" type uses (one route, one toast); Pick dates
     takes the person to the pool it just wrote; View on map is a plain search
     link — no key, no API, no spend. Dissolve / remove / add only rewrite this
     person's clusterPrefs. */

  const planCluster = async (c: FeedCluster) => {
    try {
      await createDateIdea({ title: c.label });
      setDateSaved(true);
      window.setTimeout(() => setDateSaved(false), 4000);
      await loadDates();
    } catch (e) {
      console.error('[Pear] cluster plan failed', e);
    }
  };

  /* "Pick dates" lands on Us > Dates through the app's own destination
     handler: push the hash navigateTo would push, then hand it the popstate
     the Back/Forward retrace listens on (card Mn55eOvFgvC0), so the tab, the
     hash and the browser's own history all agree on where the person is. */
  const pickClusterDates = () => {
    window.history.pushState(null, '', '#us-dates');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const dissolveCluster = (key: string) => {
    setOpenCluster(null);
    setClusterAddPick('');
    writeClusterPrefs({ ...clusterPrefs, dissolved: [...new Set([...clusterPrefs.dissolved, key])] });
  };

  const removeClusterMember = (key: string, itemId: string) => {
    const excl = clusterExclKey(key, itemId);
    writeClusterPrefs({
      ...clusterPrefs,
      excluded: clusterPrefs.excluded.includes(excl)
        ? clusterPrefs.excluded
        : [...clusterPrefs.excluded, excl],
      added: Object.fromEntries(
        Object.entries(clusterPrefs.added).map(([k, v]) => [k, v.filter(id => id !== itemId)]),
      ),
    });
  };

  const addClusterMember = (key: string, itemId: string) => {
    const excl = clusterExclKey(key, itemId);
    const cur = clusterPrefs.added[key] ?? [];
    writeClusterPrefs({
      ...clusterPrefs,
      excluded: clusterPrefs.excluded.filter(e => e !== excl),
      added: { ...clusterPrefs.added, [key]: cur.includes(itemId) ? cur : [...cur, itemId] },
    });
    setClusterAddPick('');
  };

  /** E4's escape hatch: dissolve, remove and add are all opinions, so all of
   *  them come back with one control — no group can be lost for good. */
  const restoreClusters = () => {
    writeClusterPrefs(EMPTY_CLUSTER_PREFS);
    setClusterAddPick('');
  };

  return {
    clusterPrefs, setClusterPrefs, openCluster, setOpenCluster,
    clusterAddPick, setClusterAddPick, writeClusterPrefs,
    planCluster, pickClusterDates, dissolveCluster, removeClusterMember, addClusterMember, restoreClusters,
  };
}

export type NotebookClusters = ReturnType<typeof useNotebookClusters>;
